import React, { useState, useEffect } from "react";
import "./ImageModal.css";
import { getCustomElementUrl } from "../../utils/cloudinary";
import Portal from "../portal/Portal";
import { waveform } from "ldrs";

// Cloudinary URLs for images
const closeIcon = getCustomElementUrl('cross');
const arrowLeft = getCustomElementUrl('arrow_left_image_modal');
const arrowRight = getCustomElementUrl('arrow_right_image_modal');

// Register the waveform loader
waveform.register();

interface ImageModalProps {
    imageSrc: string;
    onClose: () => void;
    objectFit?: "cover" | "contain" | "fill" | "scale-down" | "none";
    descriptiveTitle?: string;
    gallery?: {
        src: string;
        objectFit: "cover" | "contain" | "fill" | "scale-down" | "none";
        descriptiveTitle?: string;
    }[];
    currentIndex?: number;
    onNavigate?: (direction: "prev" | "next") => void;
}

const ImageModal = ({
    imageSrc,
    onClose,
    objectFit = "contain",
    descriptiveTitle,
    gallery = [],
    currentIndex = 0,
    onNavigate,
}: ImageModalProps) => {
    const [isImageLoading, setIsImageLoading] = useState(false);

    // Track current image to detect changes that should trigger loading state
    const [currentImageSrc, setCurrentImageSrc] = useState(imageSrc);

    // Set up image loading state detection
    useEffect(() => {
        if (currentImageSrc !== imageSrc) {
            setIsImageLoading(true);
            setCurrentImageSrc(imageSrc);
        }
    }, [imageSrc, currentImageSrc]);

    const handleBackgroundClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handlePrevClick = () => {
        if (onNavigate) {
            onNavigate("prev");
        }
    };

    const handleNextClick = () => {
        if (onNavigate) {
            onNavigate("next");
        }
    };

    const handleImageLoad = () => {
        setIsImageLoading(false);
    };

    const handleImageError = () => {
        setIsImageLoading(false);
    };

    // Handle keyboard navigation
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Prevent event propagation to stop it from affecting the ModalCarousel
            e.stopPropagation();

            if (e.key === "Escape") {
                onClose();
            } else if (
                e.key === "ArrowLeft" &&
                onNavigate &&
                currentIndex > 0
            ) {
                onNavigate("prev");
            } else if (
                e.key === "ArrowRight" &&
                onNavigate &&
                currentIndex < gallery.length - 1
            ) {
                onNavigate("next");
            }
        };

        // Add the event listener
        window.addEventListener("keydown", handleKeyDown, true); // Use capture phase to handle event before it bubbles

        // Clean up the event listener on unmount
        return () => {
            window.removeEventListener("keydown", handleKeyDown, true);
        };
    }, [onClose, onNavigate, currentIndex, gallery.length]);

    // Only show navigation if we have a gallery with more than 1 image and onNavigate function
    const showNavigation = gallery.length > 1 && onNavigate;

    return (
        <Portal containerId="image-modal-portal">
            <div
                className="image-modal-overlay"
                onClick={handleBackgroundClick}
            >
                <div className="image-modal-container">
                    <div className="image-modal-descriptive-title-container">
                        <div className="image-modal-descriptive-title">
                            {descriptiveTitle}
                        </div>
                    </div>
                    <div className="image-modal-x-button-container">
                        <div
                            className="image-modal-x-button-outline"
                            onClick={onClose}
                        >
                            <div className="image-modal-x-button-inner">
                                <img
                                    src={closeIcon}
                                    alt="Close"
                                    className="image-modal-x-button-icon"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Image loading indicator */}
                    {isImageLoading && (
                        <div className="image-modal-loader-container">
                            <l-waveform
                                size="40"
                                stroke="3.5"
                                speed="1"
                                color="white"
                            ></l-waveform>
                        </div>
                    )}

                    <img
                        src={imageSrc}
                        alt="Full size"
                        className="image-modal-image"
                        style={{
                            objectFit,
                            opacity: isImageLoading ? 0 : 1,
                        }}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                    />

                    {showNavigation && (
                        <div className="image-modal-navigation">
                            <button
                                className="image-modal-nav-button image-modal-nav-button-left"
                                onClick={handlePrevClick}
                                disabled={currentIndex === 0}
                            >
                                <img src={arrowLeft} alt="Previous" />
                            </button>
                            <div className="image-modal-index">
                                {currentIndex + 1} / {gallery.length}
                            </div>
                            <button
                                className="image-modal-nav-button image-modal-nav-button-right"
                                onClick={handleNextClick}
                                disabled={currentIndex === gallery.length - 1}
                            >
                                <img src={arrowRight} alt="Next" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Portal>
    );
};

export default ImageModal;
